import { Edit, X } from 'lucide-react';
import { forwardRef, useImperativeHandle, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AudioPreview } from '@/components/ui/audio-preview';
import { truncateFilename } from '@/lib/utils/truncateFilename';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface Recording {
  blob?: Blob; // Optional - for newly recorded audio
  duration: number;
  id: string; // Client-side UUID
  name: string;
  segmentId?: number; // Backend audio_segment ID (if loaded from server)
  timestamp: Date;
  url?: string; // Optional - for S3 audio files
}

export interface RecordingsListRef {
  addRecording: (recording: Recording) => void;
  getRecordings: () => Recording[];
  removeRecording: (id: string) => void;
  renameRecording: (id: string, name: string) => void;
  setRecordings: (recordings: Recording[]) => void;
}

interface RecordingsListProps {}

export const RecordingsList = forwardRef<RecordingsListRef, RecordingsListProps>((props, ref) => {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [editingRecording, setEditingRecording] = useState<Recording | null>(null);
  const [newName, setNewName] = useState('');
  const [fileExtension, setFileExtension] = useState('');

  // Expose imperative methods via ref. addRecording / setRecordings both
  // de-dup by id so concurrent recovery paths (IndexedDB replay + server
  // segment sync + live recorder callback) can't produce duplicate React keys.
  useImperativeHandle(
    ref,
    () => ({
      getRecordings: () => recordings,
      setRecordings: (newRecordings: Recording[]) => {
        const seen = new Set<string>();
        const deduped = newRecordings.filter((r) => {
          if (seen.has(r.id)) return false;
          seen.add(r.id);
          return true;
        });
        setRecordings(deduped);
      },
      addRecording: (recording: Recording) => {
        setRecordings((prev) =>
          prev.some((r) => r.id === recording.id) ? prev : [...prev, recording],
        );
      },
      removeRecording: (id: string) => {
        setRecordings((prev) => prev.filter((r) => r.id !== id));
      },
      renameRecording: (id: string, name: string) => {
        setRecordings((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
      },
    }),
    [recordings],
  );

  const handleRenameClick = (recording: Recording) => {
      // Separate filename from extension
      const lastDotIndex = recording.name.lastIndexOf('.');
      if (lastDotIndex > 0) {
        const nameWithoutExt = recording.name.slice(0, lastDotIndex);
        const ext = recording.name.slice(lastDotIndex);
        setNewName(nameWithoutExt);
        setFileExtension(ext);
      } else {
        // No extension found
        setNewName(recording.name);
        setFileExtension('');
      }

      setEditingRecording(recording);
      setRenameDialogOpen(true);
    };

    const handleRenameSubmit = () => {
    if (editingRecording && newName.trim()) {
      // Append the extension back to the new name
      const fullName = newName.trim() + fileExtension;
      // Update local state
      setRecordings((prev) => prev.map((r) => (r.id === editingRecording.id ? { ...r, name: fullName } : r)));
      setRenameDialogOpen(false);
      setEditingRecording(null);
      setNewName('');
      setFileExtension('');
    }
  };

    const handleDialogClose = () => {
      setRenameDialogOpen(false);
      setEditingRecording(null);
      setNewName('');
      setFileExtension('');
    };

  const handleDelete = (id: string) => {
    setRecordings((prev) => prev.filter((r) => r.id !== id));
  };

    // Always render component (so ref is attached), but only show UI when there are recordings
    if (recordings.length === 0) {
      return <></>;
    }

    return (
      <>
        <div className="space-y-2">
          <p className="text-sm font-medium">Saved Recordings ({recordings.length})</p>
          {recordings.map((recording) => {
            // Use URL directly if available, otherwise create object URL from blob
            const audioUrl = recording.url || (recording.blob ? URL.createObjectURL(recording.blob) : '');
            const isServerSegment = !!recording.segmentId;

            if (!audioUrl) {
              console.warn(`Recording ${recording.id} has no URL or blob, skipping`);
              return null;
            }

            return (
              <Card key={recording.id} className="p-0">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex-1 space-y-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2 min-w-0">
                        <p title={recording.name} className="text-sm font-medium truncate">
                          {truncateFilename(recording.name, 30)}
                        </p>
                        {isServerSegment && (
                          <span className="text-xs text-muted-foreground flex-shrink-0">(saved)</span>
                        )}
                      </div>

                      <div className="flex gap-2 ml-4 flex-shrink-0">
                        <Button size="sm" type="button" variant="ghost" onClick={() => handleRenameClick(recording)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" type="button" variant="ghost" onClick={() => handleDelete(recording.id)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2">
                      <AudioPreview
                        src={audioUrl}
                        onError={(err) => console.error(`Recording ${recording.id} preview error:`, err)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Rename Recording</DialogTitle>
              <DialogDescription>
                Enter a new name for your recording. The file extension will be preserved.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Recording Name</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="name"
                    autoFocus
                    value={newName}
                    className="flex-1"
                    placeholder="Enter recording name"
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleRenameSubmit();
                      }
                    }}
                  />
                  {fileExtension && (
                    <span className="text-sm text-muted-foreground font-mono whitespace-nowrap">{fileExtension}</span>
                  )}
                </div>
                {fileExtension && (
                  <p className="text-xs text-muted-foreground">
                    Full name: {newName || '...'}
                    {fileExtension}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button type="button" disabled={!newName.trim()} onClick={handleRenameSubmit}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

RecordingsList.displayName = 'RecordingsList';
